import { gettext, pgettext } from '@integro/gettext';

type MessageThunks = Record<string, () => string>;

export type Messages<T extends MessageThunks> = {
  readonly [K in keyof T]: string;
};

export function defineMessages<T extends MessageThunks>(
  thunks: T,
): Messages<T> {
  const cache: Record<string, string> = Object.create(null);
  const catalog = {} as Messages<T>;

  for (const key of Object.keys(thunks) as (keyof T & string)[]) {
    Object.defineProperty(catalog, key, {
      enumerable: true,
      get(): string {
        if (key in cache) {
          return cache[key];
        }
        const value = thunks[key]();
        if (window.$translate) {
          cache[key] = value;
        }
        return value;
      },
    });
  }

  return catalog;
}

export const gridMessages = defineMessages({
  rows: () => pgettext('ItpDataGrid', 'rows'),
  of: () => pgettext('ItpDataGrid', 'of'),
  clearAllFilters: () => pgettext('ItpDataGrid', 'Clear all filters'),
  refresh: () => pgettext('ItpDataGrid', 'Refresh'),
  export: () => pgettext('ItpDataGrid', 'Export'),
  exportXlsx: () => pgettext('ItpDataGrid', 'Download as an Excel workbook'),
  exportCsv: () => pgettext('ItpDataGrid', 'Download as a CSV file'),
  columns: () => pgettext('ItpDataGrid', 'Columns'),
  selectAll: () => pgettext('ItpDataGrid', 'Select all'),
  refreshFailed: () =>
    pgettext('ItpDataGrid', 'Refresh failed — showing the data last loaded.'),
  refreshFailedOffline: () =>
    pgettext('ItpDataGrid', 'No connection — showing the data last loaded.'),
  reload: () => pgettext('ItpDataGrid', 'Reload'),
  resizeColumn: () =>
    pgettext('ItpDataGrid', 'Drag to resize, double-click to fit the content'),
  yes: () => gettext('Yes'),
  no: () => gettext('No'),
});

export const dataTableMessages = defineMessages({
  noDataAvailable: () => gettext('No data available'),
  create: () => pgettext('ItpDataTable', 'Create'),
  total: () => pgettext('ItpDataTable', 'Total'),
  locked: () => gettext('Locked'),
  unlocked: () => gettext('Unlocked'),
  noResultFor: () => pgettext('ItpDataTable', 'No result for:'),
  typeToSearch: () => pgettext('ItpDataTable', 'Type something to search...'),
  confirmDeleteRow: () =>
    pgettext('ItpDataTable', 'Are you sure you want to delete this row?'),
});

export const validationMessages = defineMessages({
  field: () => pgettext('Validation', 'Field'),
  required: () => pgettext('Validation', '%(label)s is required.'),
  minLength: () =>
    pgettext('Validation', '%(label)s must be at least %(n)s characters.'),
  maxLength: () =>
    pgettext('Validation', '%(label)s must be at most %(n)s characters.'),
  minValue: () => pgettext('Validation', '%(label)s must be at least %(n)s.'),
  maxValue: () => pgettext('Validation', '%(label)s must be at most %(n)s.'),
  number: () => pgettext('Validation', '%(label)s must be a valid number.'),
  email: () => pgettext('Validation', '%(label)s must be a valid email.'),
  oneOf: () => pgettext('Validation', '%(label)s must be one of: %(n)s'),
  format: () => pgettext('Validation', '%(label)s format is invalid.'),
  invalid: () => pgettext('Validation', '%(label)s is invalid.'),
});

export const formMessages = defineMessages({
  required: () => pgettext('ItpForm', 'This field is required'),
  noResults: () => pgettext('ItpFormSelect', 'No results'),
  searchFailed: () => pgettext('ItpFormSelect', 'Search failed:'),
});

export const buttonMessages = defineMessages({
  ok: () => pgettext('Button', 'OK'),
  save: () => gettext('Save'),
  cancel: () => gettext('Cancel'),
  edit: () => gettext('Edit'),
  delete: () => gettext('Delete'),
  close: () => gettext('Close'),
  submit: () => pgettext('Button', 'Submit'),
});

export const modalMessages = defineMessages({
  unsavedChanges: () =>
    pgettext(
      'ItpModal',
      'You have unsaved changes. Do you want to close this dialog?',
    ),
  error: () => pgettext('MessageBox', 'Error'),
  unknownError: () => pgettext('MessageBox', 'An unknown error occurred.'),
});

export function formatMessage(
  message: string,
  values: Record<string, string | number>,
): string {
  return message.replace(/%\((\w+)\)s/g, (_, key) => String(values[key] ?? ''));
}

export const loadingMessages = defineMessages({
  loading: () => pgettext('Loading', 'Loading...'),
});

export const loadErrorMessages = defineMessages({
  failedToLoad: () => pgettext('LoadError', 'Failed to load.'),
  reload: () => pgettext('LoadError', 'Reload'),
});

export const usercontrolMessages = defineMessages({
  loading: () => pgettext('UserControlMessage', 'Loading...'),
  reloading: () => pgettext('UserControlMessage', 'Reloading...'),
});

export const resourceMetaMessages = defineMessages({
  created: () => pgettext('ItpResourceMeta', 'Created'),
  lastModified: () => pgettext('ItpResourceMeta', 'Last modified'),
});

export const routerTabMessages = defineMessages({
  close: () => pgettext('RouterTab', 'Close'),
  closeToTheLeft: () => pgettext('RouterTab', 'Close to the left'),
  closeToTheRight: () => pgettext('RouterTab', 'Close to the right'),
  closeAbove: () => pgettext('RouterTab', 'Close above'),
  closeBelow: () => pgettext('RouterTab', 'Close below'),
  closeOthers: () => pgettext('RouterTab', 'Close others'),
  closeAll: () => pgettext('RouterTab', 'Close all'),
  refresh: () => pgettext('RouterTab', 'Refresh'),
  refreshAll: () => pgettext('RouterTab', 'Refresh all'),
});

export const layoutModeMessages = defineMessages({
  tabbedLayout: () => pgettext('LayoutMode', 'Tabbed layout'),
  sidebarLayout: () => pgettext('LayoutMode', 'Sidebar layout'),
});
